// Pure DMARC record parse/build helpers.
// Extracted from the original DmarcRecordConfigurator so the managed variant can
// reuse identical behaviour without modifying the original (still used by DomainWizard).

export type DmarcKnown = {
    p: string;
    sp: string;
    np: string;
    adkim: string;
    aspf: string;
    pct: string;
    ri: string;
    rf: string;
    rua: string;
    ruf: string;
    fo: string;
    psd: string;
    t: string;
};

export type DmarcExtra = { k: string; v: string };

export type ParsedDmarc = {
    isDmarc: boolean;
    known: DmarcKnown;
    extras: DmarcExtra[];
};

const policyVals = new Set(["none", "quarantine", "reject"]);
const alignVals = new Set(["r", "s"]);
const knownKeys = new Set([
    "p",
    "sp",
    "np",
    "adkim",
    "aspf",
    "pct",
    "ri",
    "rf",
    "rua",
    "ruf",
    "fo",
    "psd",
    "t",
]);

const norm = (s?: string | null): string => (s ?? "").trim();

const splitParts = (txt?: string): string[] =>
    norm(txt)
        .split(";")
        .map((x) => x.trim())
        .filter(Boolean);

const toIntStr = (v: unknown, fallback: number): string => {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(fallback);
    return String(Math.max(0, Math.round(n)));
};

const pctNorm = (v: unknown): string => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "100";
    return String(Math.min(100, Math.max(0, Math.round(n))));
};

const foNormalize = (v?: string): string => {
    const s = norm(v);
    if (!s) return "";
    const parts = s
        .split(":")
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean);
    const allowed = new Set(["0", "1", "d", "s"]);
    const uniq: string[] = [];
    for (const p of parts) {
        if (allowed.has(p) && !uniq.includes(p)) uniq.push(p);
    }
    if (!uniq.length) return "";
    const order = ["0", "1", "d", "s"];
    uniq.sort((a, b) => order.indexOf(a) - order.indexOf(b));
    return uniq.join(":");
};

const ynNorm = (v?: string): string => {
    const s = norm(v).toLowerCase();
    return s === "y" || s === "n" ? s : "";
};

const tNorm = (v?: string): string =>
    norm(v).toLowerCase() === "y" ? "y" : "";
const rfNorm = (v?: string): string =>
    norm(v).toLowerCase() === "afrf" ? "afrf" : "";

export const foComboOptions: string[] = (() => {
    const base = ["0", "1", "d", "s"];
    const out: string[] = [];
    for (let mask = 1; mask < 1 << base.length; mask++) {
        const parts: string[] = [];
        for (let i = 0; i < base.length; i++) {
            if (mask & (1 << i)) parts.push(base[i]);
        }
        const v = parts.join(":");
        if (v.includes(":")) out.push(v);
    }
    return out;
})();

export function parseDmarc(txt?: string): ParsedDmarc {
    const parts = splitParts(txt);
    const tags: DmarcExtra[] = [];
    let isDmarc = false;

    for (const part of parts) {
        const i = part.indexOf("=");
        if (i <= 0) continue;
        const k = part.slice(0, i).trim().toLowerCase();
        const v = part.slice(i + 1).trim();

        if (k === "v") {
            isDmarc = /^dmarc1$/i.test(v);
            continue;
        }

        tags.push({ k, v });
    }

    const map = new Map<string, string>();
    for (const tag of tags) map.set(tag.k, tag.v);

    const pRaw = norm(map.get("p")).toLowerCase();
    const spRaw = norm(map.get("sp")).toLowerCase();
    const npRaw = norm(map.get("np")).toLowerCase();

    const known: DmarcKnown = {
        p: policyVals.has(pRaw) ? pRaw : "none",
        sp: policyVals.has(spRaw) ? spRaw : "",
        np: policyVals.has(npRaw) ? npRaw : "",
        pct: pctNorm(map.get("pct") ?? 100),
        ri: toIntStr(map.get("ri") ?? "86400", 86400),
        adkim: alignVals.has(norm(map.get("adkim")).toLowerCase())
            ? norm(map.get("adkim")).toLowerCase()
            : "",
        aspf: alignVals.has(norm(map.get("aspf")).toLowerCase())
            ? norm(map.get("aspf")).toLowerCase()
            : "",
        rua: map.get("rua") ?? "",
        ruf: map.get("ruf") ?? "",
        fo: foNormalize(map.get("fo") ?? ""),
        rf: rfNorm(map.get("rf") ?? ""),
        psd: ynNorm(map.get("psd") ?? ""),
        t: tNorm(map.get("t") ?? ""),
    };

    const extras = tags.filter((tag) => !knownKeys.has(tag.k));

    return { isDmarc, known, extras };
}

export function buildDmarc({
    known,
    extras,
}: {
    known: DmarcKnown;
    extras: DmarcExtra[];
}): string {
    const p = policyVals.has(known.p) ? known.p : "none";
    const sp = policyVals.has(known.sp) ? known.sp : "";
    const np = policyVals.has(known.np) ? known.np : "";
    const pct = pctNorm(known.pct);
    const ri = toIntStr(known.ri, 86400);
    const adkim = known.adkim === "s" ? "s" : known.adkim === "r" ? "r" : "";
    const aspf = known.aspf === "s" ? "s" : known.aspf === "r" ? "r" : "";
    const rua = norm(known.rua);
    const ruf = norm(known.ruf);
    const fo = foNormalize(known.fo);
    const rf = rfNorm(known.rf);
    const psd = ynNorm(known.psd);
    const t = tNorm(known.t);

    const tags = ["v=DMARC1", `p=${p}`];

    if (rua) tags.push(`rua=${rua}`);
    if (adkim) tags.push(`adkim=${adkim}`);
    if (aspf) tags.push(`aspf=${aspf}`);
    if (fo) tags.push(`fo=${fo}`);
    if (ri !== "86400") tags.push(`ri=${ri}`);
    if (pct !== "100") tags.push(`pct=${pct}`);
    if (sp) tags.push(`sp=${sp}`);
    if (np) tags.push(`np=${np}`);
    if (ruf) tags.push(`ruf=${ruf}`);
    if (rf) tags.push(`rf=${rf}`);
    if (psd) tags.push(`psd=${psd}`);
    if (t) tags.push(`t=${t}`);

    for (const x of extras) {
        if (!x?.k) continue;
        tags.push(`${x.k}=${x.v ?? ""}`);
    }

    return tags.join("; ") + ";";
}
